import React from "react";
import ReactDOM from "react-dom";
import _ from "underscore";
import * as CameraAPI from "../../../api/camera";
import { isEmpty, isNotEmpty } from "../../../framework/utils";
import { alert } from "../../../plugins";
import M from "../../../strings";
import { format, optional, uuid } from "../../../utils/lang";
import { MaterialIcon } from "../materialIcon";
import * as CustomUtils from "../../../utils/customUtils";

export default class Camera extends React.Component {
    constructor(props) {
        super(props);
        this.addElement();
        this.uuid = uuid();
        this.state = {
            showFeed: false,
            devices: [],
            currentDevice: undefined,
            pictures: [],
            zoom: 1,
            videoWidth: 0,
            videoHeight: 0
        }
        this.cameraStreamRef = React.createRef();
        this.streamCanvasRef = React.createRef();
        this.cameraPictureRef = React.createRef();

    }

    componentDidMount() {
        const documentTitle = document.title,
            locationHref = location.href;

        this.onGoBack = () => {
            history.pushState(null, documentTitle, locationHref);
            this.close();
        }

        window.addEventListener("popstate", this.onGoBack);

        CameraAPI.getVideoDevices()
            .then(data => {
                const {devices, lastDeviceId} = data;
                
                if (isEmpty(devices)) {
                    throw new Error("[Camera] componentDidMount: no video devices available");
                }

                const lastDeviceIndex = _.findIndex(devices, d => d.deviceId === lastDeviceId);

                this.setState({
                    devices: devices,
                    currentDevice: lastDeviceIndex !== -1 ? lastDeviceIndex : 0,
                }, this.startStream.bind(this));
            })
            .catch(this.onError.bind(this));


        this.cameraStreamRef.current.addEventListener("play", (this._onVideoPlay = (e) => { this.onVideoPlay(e) }))
        //this.cameraStreamRef.current.addEventListener("stop", (this._onVideoPlay = (e) => { this.onVideoStop(e) }))

        this.running = true;
        this.startRendering();
    }

    onVideoPlay(e) {
        this.setState({
            videoWidth: e.target.videoWidth,
            videoHeight: e.target.videoHeight
        });
    }

    componentWillUnmount() {
        this.running = false;

        if (isNotEmpty(this.onGoBack)) {
            window.removeEventListener("popstate", this.onGoBack);
        }

        this.stopStream();

        _.each(this.state.pictures, p => this.clearPictureUrl(p));
        
        this.removeElement();
    }

    addElement() {
        if (isNotEmpty(this.el)) {
            this.removeElement();
        }

        this.el = document.createElement("div");
        document.body.querySelector("main").appendChild(this.el);
    }

    removeElement() {
        if (isNotEmpty(this.el)) {
            this.el.remove();
            this.el = undefined;
        }
    }

    onError(e) {
        alert(M("ooops"), M("problemOccoured"))
            .then(() => {
                if (this.props.onError && _.isFunction(this.props.onError)) {
                    this.props.onError(e);
                } else if (this.props.onClose && _.isFunction(this.props.onClose)) {
                    this.props.onClose();
                }
                this.removeElement();
            })
    }

    close() {
        if (this.props.onClose && _.isFunction(this.props.onClose)) {
            this.props.onClose();
        }
        
        this.removeElement();
    }

    save() {
        const pictures = _.filter(this.state.pictures, p => p.selected);

        if (isEmpty(pictures)) {
            this.close();
        } else if (this.props.onSave && _.isFunction(this.props.onSave)) {
            this.props.onSave(_.pluck(pictures, "file"));
        }
    }

    calculatePictureSize(element, originalWidth, originalHeight) {
        let width = 0;
        let height = 0;

        if (element) {
            const widthRatio = originalHeight / originalWidth;
            const heightRatio = originalWidth / originalHeight;
            if (heightRatio >= widthRatio) {
                width = originalWidth;
                height = width / heightRatio;
            } else {
                height = originalHeight;
                width = height / widthRatio;
            }

            const desiredWidth = $(element).parent().width();
            const desiredHeight = $(element).parent().height();
            const scaleToFitParent = Math.min(desiredWidth / width, desiredHeight / height);

            width *= scaleToFitParent;
            height *= scaleToFitParent;
        }

        return {width, height};
    }

    startRendering() {
        const renderFrame = () => {
            if (!this.running) {
                return;
            }

            if (!this.streamCanvasRef.current || ! this.cameraStreamRef.current) {
                setTimeout(renderFrame, 1000);
                return;
            }

            const canvas = this.streamCanvasRef.current;
            const video = this.cameraStreamRef.current;
            const context = canvas.getContext("2d");

            const {width, height} = this.calculatePictureSize(canvas, video.videoWidth, video.videoHeight);

            canvas.width = width;
            canvas.height = height;
            
            if (video.readyState === video.HAVE_ENOUGH_DATA) { 
                context.resetTransform();
                context.translate((width * (this.state.zoom - 1)) / -2, (height * (this.state.zoom - 1)) / -2);
                context.scale(this.state.zoom, this.state.zoom);

                context.drawImage(video, 0, 0, width, height);

                context.resetTransform();
                
                requestAnimationFrame(renderFrame);
            } else {
                setTimeout(renderFrame, 1000);
            }

        }

        renderFrame();
    }

    startStream() {
        const {devices, currentDevice: currentDeviceIndex} = this.state;

        if (isEmpty(devices)) {
            const e = new Error("[Camera] start stream: no video devices found")
            this.onError(e);
        }

        const currentDevice = devices[currentDeviceIndex || 0],
            currentDeviceId = currentDevice.deviceId;

        this.setState({showFeed: true}, () => {
            CameraAPI.startStream(currentDeviceId, {maxRes: true})
                .then(stream => {
                    this.cameraStreamRef.current.srcObject = stream;
                })
                .catch(this.onError.bind(this));
        });
    }

    stopStream() {
        if (this.cameraStreamRef.current) {
            this.cameraStreamRef.current.srcObject = null;
        }

        this.setState({showFeed: false});

        return CameraAPI.stopStream();
    }

    switchStream() {
        const {devices, currentDevice: currentDeviceIndex} = this.state,
            newDeviceIndex = currentDeviceIndex === undefined || currentDeviceIndex === devices.length - 1
                ? 0
                : currentDeviceIndex + 1;

        if (devices.length <= 1) {
            return false;
        }
        
        this.setState({currentDevice: newDeviceIndex}, this.startStream.bind(this));
    }

    takePicture() {
        const successCallback = (blob) => {
            const _pictures = [...optional(this.state.pictures, [])],
                _uuid = uuid(),
                fileName = _uuid + "." + blob.type.replace("image/", ""),
                file = new File([blob], fileName, {type: blob.type}),
                url = URL.createObjectURL(blob);
            
            _pictures.push({
                file: file,
                url: url,
                uuid: _uuid,
                selected: true,
            });

            this.setState({
                pictures: _pictures,
            });

            if (_pictures.length === 1) {
                this.stopStream();
            }
        }

        CameraAPI.takePhoto({maxRes: false, zoom: this.state.zoom})
            .then(successCallback)
            .catch(e => {
                console.log("[Camera] takePicture: CameraAPI.takePhoto failed. Reverting to fallback method");
                const player = this.cameraStreamRef.current,
                    canvas = document.createElement("canvas"),
                    context = canvas.getContext("2d");

                canvas.width = player.videoWidth;
                canvas.height = player.videoHeight;

                context.resetTransform();
                context.translate((canvas.width * (this.state.zoom - 1)) / -2, (canvas.height * (this.state.zoom - 1)) / -2);
                context.scale(this.state.zoom, this.state.zoom);

                const x = 0;
                const y = 0;

                context.drawImage(player, x, y, canvas.width, canvas.height);
                try {
                    canvas.toBlob(successCallback);
                } catch (e) {
                    this.onError(e);
                }
            });
    }

    deletePicture(picture) {
        const pictures = _.without(this.state.pictures, picture);

        this.setState({pictures}, () => {
            this.clearPictureUrl(picture);
            if (this.state.pictures.length === 0) {
                this.startStream();    
            }
        });
    }

    clearPictureUrl(picture) {
        const url = picture.url;

        if (isNotEmpty(url)) {
            try {
                URL.revokeObjectURL(url);
            } catch (e) {
                console.log(e);
            }
        }
    }

    getActions() {
        const {showFeed, devices, pictures} = this.state,
            lastPicture = this.getLastPicture(),
            items = [];

        if (showFeed) {
            items.push(<MaterialIcon
                key={"camera-" + this.uuid + "-action-" + (items.length + 1)}
                icon="radio_button_checked"
                onClick={this.takePicture.bind(this)}
            />)

            if (isNotEmpty(devices) && devices.length > 1) {
                items.push(<MaterialIcon
                    key={"camera-" + this.uuid + "-action-" + (items.length + 1)}
                    icon="flip_camera_ios"
                    onClick={this.switchStream.bind(this)}
                />)
            }
        }

        if (isNotEmpty(pictures)) {
            items.push(<MaterialIcon
                key={"camera-" + this.uuid + "-action-" + (items.length + 1)}
                icon="check_circle"
                onClick={this.save.bind(this)}
            />);
        }

        if (!showFeed) {
            if (isNotEmpty(lastPicture)) {
                items.push(<MaterialIcon
                    key={"camera-" + this.uuid + "-action-" + (items.length + 1)}
                    icon="delete"
                    onClick={this.deletePicture.bind(this, lastPicture)}
                />);
            }

            items.push(<MaterialIcon
                key={"camera-" + this.uuid + "-action-" + (items.length + 1)}
                icon="add_photo_alternate"
                onClick={this.startStream.bind(this)}
            />);
        }

        return items;
    }

    getLastPicture() {
        return _.last(this.state.pictures);
    }

    toggleSelection(picture) {
        const pictures = [...this.state.pictures],
            pictureToChange = _.findWhere(pictures, {uuid: picture.uuid});

        if (isEmpty(pictureToChange)) {
            return false;
        }

        pictureToChange.selected = !pictureToChange.selected;

        this.setState({pictures: pictures});
    }

    onSetZoom(e, zoom) {
        e.preventDefault();
        e.stopPropagation();

        this.setState({zoom});
    }

    renderContent() {
        const showFeed = this.state.showFeed,
            actions = this.getActions(),
            pictures = this.state.pictures,
            lastPicture = this.getLastPicture();

        const pictureSize = this.calculatePictureSize(
            optional(this.streamCanvasRef.current, this.cameraPictureRef.current), 
            this.state.videoWidth, 
            this.state.videoHeight
        );

        return (<div
            className="camera"
        >
            <div
                className="camera__close"
            >
                <MaterialIcon
                    icon="close"
                    onClick={this.close.bind(this)}
                />
            </div>
            
            <div
                className="camera__preview"
            >
                {showFeed || _.isEmpty(lastPicture) ? (
                    <>
                        <div className="camera__zoom">
                            <ul>
                                <li><a href="#" className={this.state.zoom == 1 ? "active" : ""} onClick={(e) => this.onSetZoom(e, 1)}>1</a></li>
                                <li><a href="#" className={this.state.zoom == 1.5 ? "active" : ""} onClick={(e) => this.onSetZoom(e, 1.5)}>1.5</a></li>
                                <li><a href="#" className={this.state.zoom == 2 ? "active" : ""} onClick={(e) => this.onSetZoom(e, 2)}>2</a></li>
                                <li><a href="#" className={this.state.zoom == 2.5 ? "active" : ""} onClick={(e) => this.onSetZoom(e, 2.5)}>2.5</a></li>
                            </ul>    
                        </div>
                        <video
                            ref={this.cameraStreamRef}
                            id="player"
                            style={{display: "none"}}
                            autoPlay
                        />
                        <canvas
                            ref={this.streamCanvasRef}
                            id="player-canvas"
                        />
                        {isNotEmpty(pictures) && (
                            <div
                                className="camera__preview-picture-count"
                            >
                                <button
                                    className="btn btn-outline-light"
                                    onClick={this.stopStream.bind(this)}
                                >
                                    {format(M("picturesCount"), pictures.length)}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <img
                        ref={this.cameraPictureRef}
                        className="camera__picture"
                        src={lastPicture.url}
                        style={{
                            width: pictureSize.width,
                            height: pictureSize.height
                        }}
                    />
                )}
            </div>
            <div
                className="camera__actions"
            >
                {actions}
            </div>
            {(showFeed && isNotEmpty(pictures) || !showFeed && pictures.length > 1) && <div
                className="camera__pictures"
            >
                {_.map(pictures, picture => {
                    const parentUuid = this.uuid,
                        {uuid, url, selected} = picture;
                    return (<div
                        className="camera__picture-wrapper"
                    >
                        <div
                            key={"camera-" + parentUuid + "-thumbnail-" + uuid}
                            className={"camera__picture-thumbnail" + (selected ? " camera__picture-thumbnail--selected" : "")}
                            style={{backgroundImage: 'url(' + url + ')'}}
                            onClick={this.toggleSelection.bind(this, picture)}
                        >
                            <MaterialIcon
                                icon={selected ? "radio_button_checked" : "radio_button_unchecked"}
                            />
                        </div>
                    </div>)
                })}
            </div>}
        </div>)
    }

    render() {
        const content = this.renderContent();

        return isNotEmpty(this.el) ? ReactDOM.createPortal(content, this.el) : <></>;
    }
}